import EventTarget from '@ungap/event-target';
import Debug from 'debug';
import io from 'socket.io-client';
import {
  authenticationFailedEvent,
  connectEvent,
  disconnectEvent,
  newSocketItemEvent,
  reconnectAttemptEvent,
  reconnectFailedEvent,
  connectErrorEvent,
  reconnectErrorEvent,
} from './events';
import type { FeedItem } from './types';
import {
  LivefireCardType,
  livefireItemTypes,
  validateStringNotEmpty,
  validateStringNotEmptyOrIsURL,
} from '@/constants';
import { arrayUtilities, getRandomIntInclusive } from '@/functions';

const debug = Debug('livefire:socket-manager'); // eslint-disable-line new-cap

export const eventTypes = [
  'authentication_failed',
  'connect',
  'connect_error',
  'connect_timeout',
  'disconnect',
  'newItem',
  'reconnect_attempt',
  'reconnect_error',
  'reconnect_failed',
] as const;

const fiveMinutesMs = 5 * 60 * 1000;

type SocketManagerOptions = {
  cardTypes: LivefireCardType[] | null;
  clientId: string | null;
  token: string | null;
  url: URL | string | null;
  feeds: string[];
};

class SocketManager {
  protected socketManagerCardTypes: LivefireCardType[] = [];

  #clientId: string | null = null;

  protected disconnectTime: Date | null = null;

  protected emitter = new EventTarget();

  protected feeds = ['default'];

  protected lastConnectError: unknown | null = null;

  protected lastConnectTimeout: unknown | null = null;

  protected lastReconnectAttempt: number | null = null;

  protected lastReconnectError: unknown | null = null;

  protected retryCount = 0;

  protected socket: ReturnType<typeof io> | null = null;

  protected timeoutId: ReturnType<typeof setTimeout> | null = null;

  #token: string | null = null;

  #url: URL | string | null = null;

  constructor({
    cardTypes = [],
    clientId = null,
    token = null,
    url = null,
    feeds = ['default'],
  }: Partial<SocketManagerOptions> = {}) {
    debug('new SocketManager', clientId, url);
    this.clientId = clientId;
    this.token = token;
    this.url = url;
    this.feeds = feeds;

    if (cardTypes) {
      this.setCardTypes(cardTypes);
    }
  }

  get cardTypes() {
    return this.socketManagerCardTypes;
  }

  get clientId() {
    return this.#clientId || null;
  }

  set clientId(value) {
    if (value) {
      validateStringNotEmpty(value, 'clientId');
    }
    this.#clientId = value;
  }

  get connected() {
    return this.initialized && this.socket && this.socket.connected;
  }

  get disconnected() {
    return !this.initialized || (this.socket && this.socket.disconnected);
  }

  get initialized() {
    return !!this.socket;
  }

  get token() {
    return this.#token || null;
  }

  set token(value) {
    if (value) {
      validateStringNotEmpty(value, 'token');
    }
    this.#token = value;
  }

  get url() {
    return this.#url || null;
  }

  set url(value) {
    if (value) {
      validateStringNotEmptyOrIsURL(value, 'url');
      this.#url = value.toString();
    } else {
      this.#url = null;
    }
  }

  addEventListener(
    type: (typeof eventTypes)[number],
    listener: Parameters<EventTarget['addEventListener']>[1],
  ) {
    if (!eventTypes.includes(type)) {
      throw new Error(`Unrecognized event type name: ${type}`);
    }

    debug('addEventListener', type, listener);
    this.emitter.addEventListener(type, listener, { passive: true });
  }

  connect() {
    if (!this.token) {
      throw new Error('Need to set a token before calling connect()');
    }

    // Skip connecting if we're already connected!
    if (this.connected) {
      console.warn('connect called when socket already connected.');
      return;
    }

    if (this.socket) {
      debug('connect existing socket', this.socket);
      this.socket.open();
      return;
    }

    if (!this.url) {
      throw new Error('Need to set a URL before calling connect()');
    }

    debug('create new socket');
    const socket = io(this.url.toString(), {
      autoConnect: false,
      extraHeaders: {
        Authorization: `Bearer ${this.#token}`,
      },
      randomizationFactor: 0.5,
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 100,
      reconnectionDelayMax: 6000,
      transports: ['websocket'],
    });

    debug('register socket handlers');
    socket.on('authentication_failed', this.onAuthenticationFailed);
    socket.on('connect', this.onConnect);
    socket.on('connect_error', this.onConnectError);
    socket.on('connect_timeout', this.onConnectTimeout);
    socket.on('disconnect', this.onDisconnect);
    socket.on('newItem', this.onNewItem);
    socket.on('reconnect_attempt', this.onReconnectAttempt);
    socket.on('reconnect_error', this.onReconnectError);
    socket.on('reconnect_failed', this.onReconnectFailed);

    socket.open();
    debug('socket opened', socket);

    this.socket = socket;
  }

  protected clearTimeout = () => {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
  };

  disconnect() {
    this.retryCount = 0;
    this.clearTimeout();
    if (this.socket) {
      this.socket.close();
    }
  }

  reconnectWithBackoff() {
    this.retryCount++;

    // increase the reconnect attempt each time around up to 30 minutes (+/- 5 minutes).
    const jitterSeconds = getRandomIntInclusive(-300, 300);
    const interval = Math.min(this.retryCount, 3) * 10 * 60 * 1000 + jitterSeconds * 1000;
    debug('next retry scheduled', interval);
    this.timeoutId = setTimeout(() => {
      debug('attempting reconnect');
      this.connect();
    }, interval);
  }

  removeEventListener(
    type: (typeof eventTypes)[number],
    listener: Parameters<EventTarget['removeEventListener']>[1],
  ) {
    if (!eventTypes.includes(type)) {
      throw new Error(`Unrecognized event type name: ${type}`);
    }

    this.emitter.removeEventListener(type, listener);
  }

  reset() {
    this.disconnect();
    this.socket = null;
  }

  setCardTypes(cardTypes: LivefireCardType[]) {
    if (!arrayUtilities.areAllItemsInCollection(cardTypes, [...livefireItemTypes.items])) {
      throw new Error('cardTypes must be an array of types from the LivefireItemTypes.');
    }

    const differences = arrayUtilities.diffArray(cardTypes, this.socketManagerCardTypes);

    if (!differences.length) {
      return;
    }

    this.socketManagerCardTypes = [...cardTypes];
    if (this.connected) {
      this.disconnect();
      this.connect();
    }
  }

  /**
   * Subscribe to a feed
   */
  subscribe(feedName: string, types: string[] | null = null) {
    const data: Record<string, unknown> = {
      feedName,
      token: this.#token,
    };

    if (types && types.length) {
      // EX-3605: we need to request all item types and filter them out later for ... reasons.
      data.types = [...types];
    }

    debug('subscribe', data);
    this.socket?.emit('feedSubscribe', data);
  }

  subscribeToFeeds() {
    this.feeds.forEach((feedName) => {
      const fullFeedName =
        feedName !== 'default' ? `${this.clientId}_${feedName}` : `${this.clientId}`;
      if (this.socketManagerCardTypes.length) {
        this.subscribe(fullFeedName, [...livefireItemTypes.items]);
      } else {
        this.subscribe(fullFeedName);
      }
    });
  }

  protected onAuthenticationFailed = (data: unknown) => {
    const evt = authenticationFailedEvent(this, data);
    debug('authentication_failed', evt);
    this.emitter.dispatchEvent(evt);
  };

  protected onConnect = () => {
    this.subscribeToFeeds();

    const evt = connectEvent(this);

    this.lastReconnectAttempt = null;
    this.lastReconnectError = null;
    this.lastConnectError = null;
    this.lastConnectTimeout = null;
    this.retryCount = 0;

    debug('connect', evt);
    this.emitter.dispatchEvent(evt);
  };

  protected onConnectError = (reason: unknown) => {
    debug('connect_error', reason);

    this.lastConnectError = reason;
    this.emitter.dispatchEvent(connectErrorEvent(this, reason));
  };

  protected onConnectTimeout = (reason: unknown) => {
    debug('connect_timeout', reason);

    this.lastConnectTimeout = reason;
    this.emitter.dispatchEvent(connectErrorEvent(this, reason));
  };

  protected onDisconnect = (reason: unknown) => {
    const evt = disconnectEvent(this, reason);

    this.lastReconnectAttempt = null;
    this.lastReconnectError = null;

    let type = 'unknown';
    if (reason === 'io server disconnect') {
      type = 'server';
    } else if (reason === 'io client disconnect') {
      type = 'client';
    } else if (reason === 'transport close') {
      type = 'network';
    } else if (reason === 'ping timeout') {
      type = 'ping';
    }

    if (type !== 'server') {
      debug('disconnect', evt);
      this.emitter.dispatchEvent(evt);
      return;
    }

    // attempt to reconnect (almost) immediately if we were disconnected from the server
    // and we haven't seen a disconnect before or if the last disconnect was greater than
    // 5 minutes ago. N.B. the '+' before the Date objects are required to make TypeScript
    // happy and avoid pointless TS2362/TS2363 errors with arithmetic. 🥴
    const now = new Date();
    if (!this.disconnectTime || +now - +this.disconnectTime > fiveMinutesMs) {
      const jitter = getRandomIntInclusive(0, 60);
      debug('server disconnect, first time. using reconnect jitter', jitter);

      this.disconnectTime = now;
      setTimeout(() => this.connect(), jitter * 1000);
      return;
    }

    debug('server disconnected, aggressively. falling back to reconnect with backoff logic.');
    this.reconnectWithBackoff();
  };

  protected onNewItem = (item: FeedItem) => {
    debug('newItem', item);

    const evt = newSocketItemEvent(this, item);
    this.emitter.dispatchEvent(evt);
  };

  protected onReconnectAttempt = (attempt: number) => {
    debug('reconnect_attempt', attempt);

    if (!this.socket) {
      return;
    }

    // add back in the polling transport if it's not there in case the failure reason was
    // caused by the websocket itself being blocked (proxy, firewall, etc.) based on their
    // recommendations: https://socket.io/docs/client-api/#With-websocket-transport-only
    const { socket } = this;
    if (socket.io && !socket.io.opts.transports?.includes('polling')) {
      socket.io.opts.transports = ['polling', ...(socket.io.opts.transports ?? [])];
    }

    this.lastReconnectAttempt = attempt;
    this.emitter.dispatchEvent(reconnectAttemptEvent(this, attempt));
  };

  protected onReconnectError = (error: unknown) => {
    debug('reconnect_error', error);

    this.lastReconnectError = error;
    this.emitter.dispatchEvent(reconnectErrorEvent(this));
  };

  protected onReconnectFailed = () => {
    debug('reconnect_failed');

    const evt = reconnectFailedEvent(
      this,
      this.lastReconnectAttempt ?? -1,
      this.lastReconnectError,
    );
    this.emitter.dispatchEvent(evt);
    this.reconnectWithBackoff();
  };
}

export default SocketManager;
